import { useState, useEffect } from 'react';
import { clientService, type Client } from '../services/clientService';
import { useAuth } from '../context/AuthContextType';
import PageHeader from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

interface CreateClientRequest {
  company_name: string;
  domain?: string;
  subscription_tier?: string;
  max_users?: number;
  max_projects?: number;
}

export default function SettingsClients() {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState<CreateClientRequest>({
    company_name: '',
    domain: '',
    subscription_tier: 'basic',
    max_users: 10,
    max_projects: 10,
  });

  const canManageClients = user && ['SysAdmin', 'DevOps'].includes(user.role || '');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientService.getAll();
      setClients(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await clientService.create(createFormData);
      setShowCreateModal(false);
      setCreateFormData({
        company_name: '',
        domain: '',
        subscription_tier: 'basic',
        max_users: 10,
        max_projects: 10,
      });
      await loadClients();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create client');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) return <div className="px-6 py-4 text-sm text-slate-400">Loading clients...</div>;
  if (error) return <div className="px-6 py-4 text-sm text-red-600">{error}</div>;

  const fieldCls = "w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:border-primary-400";

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title="Client Management"
        actions={
          canManageClients ? (
            <Button size="sm" onClick={() => setShowCreateModal(true)}>Create Client</Button>
          ) : undefined
        }
      />

      <div className="px-6 py-4">
        {clients.length === 0 ? (
          <p className="text-sm text-slate-400">No clients found. Create your first client organization.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-100">
                {['Company', 'Domain', 'Subscription', 'Max Users', 'Max Projects', 'Created'].map(h => (
                  <th key={h} className="text-left py-1.5 pr-4 text-xs font-medium text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map(client => (
                <tr key={client.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="py-1.5 pr-4 text-slate-700 font-medium">{client.company_name}</td>
                  <td className="py-1.5 pr-4 text-slate-500 text-xs font-mono">{client.domain || '—'}</td>
                  <td className="py-1.5 pr-4">
                    <span className="text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{client.subscription_tier || '—'}</span>
                  </td>
                  <td className="py-1.5 pr-4 text-slate-500 text-xs">{client.max_users ?? '—'}</td>
                  <td className="py-1.5 pr-4 text-slate-500 text-xs">{client.max_projects ?? '—'}</td>
                  <td className="py-1.5 text-slate-400 text-xs">{client.created_date ? formatDate(client.created_date) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Client</DialogTitle></DialogHeader>
          <form id="create-client-form" onSubmit={handleCreateClient} className="space-y-3 py-2">
            {([
              { label: 'Company Name *', field: 'company_name', type: 'text', required: true, maxLength: 200 },
              { label: 'Domain', field: 'domain', type: 'text', placeholder: 'example.com', maxLength: 200 },
            ] as const).map(({ label, field, ...rest }) => (
              <div key={field} className="grid grid-cols-3 items-center gap-3">
                <label className="text-sm text-slate-500 text-right">{label}</label>
                <input
                  className={`col-span-2 ${fieldCls}`}
                  {...rest}
                  value={(createFormData as unknown as Record<string, string>)[field]}
                  onChange={(e) => setCreateFormData({ ...createFormData, [field]: e.target.value })}
                />
              </div>
            ))}
            <div className="grid grid-cols-3 items-center gap-3">
              <label className="text-sm text-slate-500 text-right">Subscription *</label>
              <select className={`col-span-2 ${fieldCls}`} required value={createFormData.subscription_tier}
                onChange={(e) => setCreateFormData({ ...createFormData, subscription_tier: e.target.value })}>
                <option value="basic">Basic</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="grid grid-cols-3 items-center gap-3">
              <label className="text-sm text-slate-500 text-right">Max Users</label>
              <input type="number" min={1} max={10000} className={`col-span-2 ${fieldCls}`}
                value={createFormData.max_users}
                onChange={(e) => setCreateFormData({ ...createFormData, max_users: parseInt(e.target.value) })} />
            </div>
            <div className="grid grid-cols-3 items-center gap-3">
              <label className="text-sm text-slate-500 text-right">Max Projects</label>
              <input type="number" min={1} max={10000} className={`col-span-2 ${fieldCls}`}
                value={createFormData.max_projects}
                onChange={(e) => setCreateFormData({ ...createFormData, max_projects: parseInt(e.target.value) })} />
            </div>
          </form>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button type="submit" form="create-client-form">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

